import { useEffect, useRef, useState } from "react";

type FetcherState = "idle" | "loading" | "submitting";

/**
 * Which half of a fetcher submission outlived the deadline — derived from the
 * fetcher's CURRENT `state`, never from `data`:
 *
 * - `"submit"`       — `state === "submitting"`: the action request itself is
 *                      still in flight. `fetcher.data` is NOT the current
 *                      submission's result here: RR7 keeps the PRIOR
 *                      submission's payload during a resubmission and
 *                      `fetcher.reset()` leaves `null`, so presence of data says
 *                      nothing about this POST (the same reason the
 *                      AddedToBasketFlyout gates on `state !== "submitting"`).
 * - `"revalidation"` — `state === "loading"`: the action has returned (RR7
 *                      publishes its result on the loading fetcher) and only the
 *                      post-action revalidation is still open.
 */
export type FetcherDeadlinePhase = "submit" | "revalidation";

/**
 * Deadline for a fetcher submission that never settles (dev-regression triage
 * 2026-09-09, symptom 1 — recs-popup add-to-cart "loader stays stuck").
 *
 * A `fetcher.submit()` whose request hangs (COM-67 parked-invocation class, an
 * edge/network stall) — or whose component-side state never catches up with the
 * router (the recs-popup case: router idle, tile still `loading`) — leaves
 * `fetcher.state` non-idle forever. RR7 has no timeout of its own, so a control
 * that derives "pending" from `fetcher.state !== "idle"` renders disabled
 * indefinitely.
 *
 * Spans, not busy-ness: a deadline is armed per SUBMISSION. A new span starts
 * when the fetcher leaves `idle`, and again whenever it re-enters `submitting`
 * from `loading` (a resubmission while the previous revalidation is still
 * open). Each span gets its own timer and its own `onDeadline(phase)` call, so
 * a retry that hangs after a prior stuck revalidation is still caught — the
 * first round-1 review finding. `submitting → loading` within one submission
 * keeps the running timer (one span). Returning to `idle` clears everything.
 *
 * `timedOut` is true from the moment the current span's deadline fires until
 * the fetcher settles or a new span starts, so the caller can stop treating the
 * span as pending (re-enable the control) while deciding — from `phase` — what
 * to do about it (reset + toast for a hung POST, silence for a slow
 * revalidation).
 */
export function useFetcherDeadline(
  fetcher: { state: FetcherState },
  deadlineMs: number,
  onDeadline: (phase: FetcherDeadlinePhase) => void,
): { timedOut: boolean } {
  const [timedOut, setTimedOut] = useState(false);
  const handlerRef = useRef(onDeadline);
  handlerRef.current = onDeadline;
  // The timer callback must classify against the state at FIRE time, not the
  // state captured when the span was armed (submitting → loading in between).
  const stateRef = useRef<FetcherState>(fetcher.state);
  stateRef.current = fetcher.state;
  const prevStateRef = useRef<FetcherState>("idle");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const prev = prevStateRef.current;
    const cur = fetcher.state;
    prevStateRef.current = cur;
    const clearTimer = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    if (cur === "idle") {
      clearTimer();
      // Functional update: no re-render when already false.
      setTimedOut(t => (t ? false : t));
      return;
    }

    const newSpan =
      prev === "idle" || (cur === "submitting" && prev !== "submitting");
    if (!newSpan) return; // submitting → loading: same submission, keep the timer

    clearTimer();
    setTimedOut(t => (t ? false : t));
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const stateAtFire = stateRef.current;
      // Settled between a render and its idle-effect — nothing to report.
      if (stateAtFire === "idle") return;
      setTimedOut(true);
      handlerRef.current(
        stateAtFire === "submitting" ? "submit" : "revalidation",
      );
    }, deadlineMs);
  }, [fetcher.state, deadlineMs]);

  // Unmount: never fire into an unmounted component.
  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    },
    [],
  );

  return { timedOut };
}
